import React, { useState, useContext, useRef, useEffect } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, btnP, btnSec, sp, rad, type } from "../../constants/styles";
import Fld from "./Fld";
import Ic from "./Ic";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

export default function ProfilePage() {
  const { t, h, go, role, currUserId, tens, setTens, sps, setSps, flash } = useContext(Ctx);
  const isTenant = role === "tenant";
  const isService = role === "service";

  const me = isTenant
    ? tens.find(x => x.id === currUserId)
    : isService
    ? sps.find(x => x.id === currUserId)
    : null;

  const [name, setName] = useState(me?.name || "");
  const [phone, setPhone] = useState(me?.ph || "");
  const [skill, setSkill] = useState(me?.skill || "");
  const [saving, setSaving] = useState(false);

  // Tenant rental-agreement fields (auto-fills the landlord's draft
  // agreement so they don't have to retype per-tenant). id_number stores
  // Aadhaar last-4 only — we never ask for or store the full 12 digits.
  const [parentName, setParentName] = useState(me?.parent_name || "");
  const [relation, setRelation] = useState(me?.relation || "son of");
  const [aadhaarLast4, setAadhaarLast4] = useState(me?.id_number || "");
  const [rentAddress, setRentAddress] = useState(me?.address || "");
  const [lat, setLat] = useState(me?.latitude ? parseFloat(me.latitude) : me?.lat ? parseFloat(me.lat) : null);
  const [lng, setLng] = useState(me?.longitude ? parseFloat(me.longitude) : me?.lng ? parseFloat(me.lng) : null);
  const [showMap, setShowMap] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerRef = useRef(null);

  // Initialize map when showMap toggles
  useEffect(() => {
    if (!showMap || !mapRef.current || mapInstance.current) return;
    const defLat = lat || 9.4441;
    const defLng = lng || 77.5597;
    const map = L.map(mapRef.current).setView([defLat, defLng], lat ? 15 : 12);
    L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png", { maxZoom: 19, attribution: '&copy; <a href="https://carto.com/">CARTO</a>' }).addTo(map);

    const icon = L.divIcon({ className: "", html: `<div style="width:28px;height:28px;background:#3b82f6;border:3px solid #fff;border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,0.3)"></div>`, iconSize: [28, 28], iconAnchor: [14, 14] });

    if (lat && lng) {
      markerRef.current = L.marker([lat, lng], { icon, draggable: true }).addTo(map);
      markerRef.current.on("dragend", () => {
        const p = markerRef.current.getLatLng();
        setLat(parseFloat(p.lat.toFixed(7)));
        setLng(parseFloat(p.lng.toFixed(7)));
      });
    }

    map.on("click", (e) => {
      const { lat: cLat, lng: cLng } = e.latlng;
      setLat(parseFloat(cLat.toFixed(7)));
      setLng(parseFloat(cLng.toFixed(7)));
      if (markerRef.current) { markerRef.current.setLatLng([cLat, cLng]); }
      else { markerRef.current = L.marker([cLat, cLng], { icon, draggable: true }).addTo(map); markerRef.current.on("dragend", () => { const p = markerRef.current.getLatLng(); setLat(parseFloat(p.lat.toFixed(7))); setLng(parseFloat(p.lng.toFixed(7))); }); }
    });

    mapInstance.current = map;
    setTimeout(() => map.invalidateSize(), 200);

    return () => { map.remove(); mapInstance.current = null; markerRef.current = null; };
  }, [showMap]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update map marker when lat/lng changes (e.g. from GPS)
  useEffect(() => {
    if (!mapInstance.current || !showMap) return;
    if (lat && lng && markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
      mapInstance.current.setView([lat, lng], mapInstance.current.getZoom());
    }
  }, [lat, lng, showMap]);

  const useMyLocation = () => {
    if (!navigator.geolocation) { flash("GPS not supported", "error"); return; }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const newLat = parseFloat(pos.coords.latitude.toFixed(7));
        const newLng = parseFloat(pos.coords.longitude.toFixed(7));
        setLat(newLat);
        setLng(newLng);
        setGpsLoading(false);
        if (mapInstance.current) {
          mapInstance.current.setView([newLat, newLng], 16);
          const icon = L.divIcon({ className: "", html: `<div style="width:28px;height:28px;background:#3b82f6;border:3px solid #fff;border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,0.3)"></div>`, iconSize: [28, 28], iconAnchor: [14, 14] });
          if (markerRef.current) markerRef.current.setLatLng([newLat, newLng]);
          else { markerRef.current = L.marker([newLat, newLng], { icon, draggable: true }).addTo(mapInstance.current); markerRef.current.on("dragend", () => { const p = markerRef.current.getLatLng(); setLat(parseFloat(p.lat.toFixed(7))); setLng(parseFloat(p.lng.toFixed(7))); }); }
        }
        flash("Location captured!");
      },
      () => { setGpsLoading(false); flash("Location access denied", "error"); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const save = async () => {
    if (!name.trim()) { flash(t.fillRequired || "Name is required", "error"); return; }
    if (isService && (!lat || !lng)) { flash("Please pin your shop location on the map", "error"); return; }
    setSaving(true);
    try {
      const data = { name: name.trim(), phone: phone || me.ph, skill: skill || undefined };
      if (isService && lat && lng) { data.latitude = lat; data.longitude = lng; }
      if (isTenant) {
        // Include the four rental-agreement fields. Server COALESCE()
        // skips nulls, so empty strings here leave the old value alone.
        data.parent_name = parentName.trim() || null;
        data.relation = relation || null;
        // Keep just the last 4 digits regardless of what the tenant types
        data.id_number = String(aadhaarLast4 || "").replace(/\D/g, "").slice(-4) || null;
        data.address = rentAddress.trim() || null;
      }
      const res = await api.updateProfile(data);
      if (res.success) {
        if (isTenant) {
          setTens(prev => prev.map(x => x.id === currUserId ? {
            ...x,
            name: name.trim(),
            ph: phone || x.ph,
            parent_name: data.parent_name ?? x.parent_name,
            relation: data.relation ?? x.relation,
            id_number: data.id_number ?? x.id_number,
            address: data.address ?? x.address,
          } : x));
        } else if (isService) {
          setSps(prev => prev.map(x => x.id === currUserId ? { ...x, name: name.trim(), ph: phone || x.ph, skill: skill || x.skill, latitude: lat, longitude: lng } : x));
        }
        flash(t.updated || "Profile updated!");
      } else {
        flash(res.message || "Failed to update", "error");
      }
    } catch (e) {
      flash(t.loginFail || "Failed to update", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!me) {
    return <div style={{ paddingTop: sp.lg }}>
      <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", ...type.sm, marginBottom: sp.md, display: "flex", alignItems: "center", gap: sp.xs }}>
        <Ic n="arrow-left" s={14} c={h.ac} /> {t.back}
      </button>
      <div style={{ ...crd(h), textAlign: "center", padding: sp.xxxl }}>
        <Ic n="user" s={32} c={h.tt} />
        <p style={{ ...type.sm, color: h.tt, marginTop: sp.md }}>Profile not found</p>
      </div>
    </div>;
  }

  return <div style={{ paddingTop: sp.lg }}>
    <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", ...type.sm, marginBottom: sp.md, display: "flex", alignItems: "center", gap: sp.xs }}>
      <Ic n="arrow-left" s={14} c={h.ac} /> {t.back}
    </button>

    <h2 style={{ ...type.h1, color: h.tx, margin: `0 0 ${sp.lg}px` }}>{t.profile || "Profile"}</h2>

    {/* Avatar */}
    <div style={{ display: "flex", justifyContent: "center", marginBottom: sp.xl }}>
      <div style={{ width: 72, height: 72, borderRadius: "50%", background: `${h.ac}15`, display: "flex", alignItems: "center", justifyContent: "center", border: `2px solid ${h.ac}30` }}>
        <Ic n="user" s={32} c={h.ac} />
      </div>
    </div>

    <div style={{ ...crd(h), padding: sp.xl }}>
      <Fld l={t.yourName || "Name"} v={name} set={setName} h={h} ph={t.yourName || "Your name"} />
      <Fld l={t.phone || "Phone"} v={phone} set={setPhone} h={h} ph="+91 98765 43210" />
      {isService && <Fld l={t.skill || "Skill"} v={skill} set={setSkill} h={h} ph="e.g. Plumbing" />}

      {/* Tenant rental-agreement fields — fill once, auto-populates every
          landlord's draft agreement so they don't have to ask / retype.
          Everything is optional: leave blank if you'd rather the landlord
          fill it themselves. */}
      {isTenant && (
        <div style={{ marginTop: sp.md, paddingTop: sp.md, borderTop: `1px dashed ${h.bd}` }}>
          <div style={{ ...type.xs, color: h.ts, fontWeight: 600, marginBottom: sp.xs, textTransform: "uppercase", letterSpacing: 0.3 }}>
            Rental agreement info
          </div>
          <div style={{ ...type.xs, color: h.tt, marginBottom: sp.md, lineHeight: 1.4 }}>
            Shared with your landlord so they can draft an agreement without
            asking you for the same details every time. Optional.
          </div>

          <Fld
            l="Parent / Guardian name"
            v={parentName}
            set={setParentName}
            h={h}
            ph="e.g. Ramakrishnan"
          />

          <div style={{ marginBottom: sp.md }}>
            <label style={{ ...type.xs, color: h.ts, fontWeight: 600, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.3 }}>
              Relation
            </label>
            <select
              value={relation}
              onChange={(e) => setRelation(e.target.value)}
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 12,
                border: `1px solid ${h.bd}`,
                background: h.ib,
                color: h.tx,
                fontSize: 15,
                outline: "none",
                boxSizing: "border-box",
              }}
            >
              <option value="son of">Son of</option>
              <option value="daughter of">Daughter of</option>
              <option value="wife of">Wife of</option>
              <option value="husband of">Husband of</option>
              <option value="child of">Child of</option>
            </select>
          </div>

          <Fld
            l="Aadhaar (last 4 digits)"
            v={aadhaarLast4}
            set={(v) => setAadhaarLast4(String(v || "").replace(/\D/g, "").slice(0, 4))}
            h={h}
            ph="1426"
          />

          <div style={{ marginBottom: sp.md }}>
            <label style={{ ...type.xs, color: h.ts, fontWeight: 600, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.3 }}>
              Permanent address
            </label>
            <textarea
              value={rentAddress}
              onChange={(e) => setRentAddress(e.target.value)}
              placeholder="House no., street, city, state, PIN"
              rows={3}
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 12,
                border: `1px solid ${h.bd}`,
                background: h.ib,
                color: h.tx,
                fontSize: 15,
                outline: "none",
                boxSizing: "border-box",
                resize: "vertical",
                fontFamily: "inherit",
              }}
            />
          </div>
        </div>
      )}

      {/* Service Person Location */}
      {isService && <div style={{ marginBottom: sp.md }}>
        <label style={{ ...type.xs, color: h.ts, fontWeight: 600, display: "block", marginBottom: sp.sm }}>
          <Ic n="map-pin" s={12} c={h.ac} /> Shop / Work Location <span style={{ color: h.rd }}>*</span>
        </label>

        {lat && lng ? (
          <div style={{ background: `${h.gn}08`, border: `1px solid ${h.gn}20`, borderRadius: rad.sm, padding: sp.md, marginBottom: sp.sm }}>
            <div style={{ display: "flex", alignItems: "center", gap: sp.sm }}>
              <Ic n="check-circle" s={14} c={h.gn} />
              <span style={{ ...type.sm, color: h.gn, fontWeight: 600 }}>Location pinned</span>
            </div>
            <div style={{ ...type.xs, color: h.ts, marginTop: sp.xs }}>{lat.toFixed(4)}, {lng.toFixed(4)}</div>
          </div>
        ) : (
          <div style={{ background: `${h.wn}08`, border: `1px solid ${h.wn}20`, borderRadius: rad.sm, padding: sp.md, marginBottom: sp.sm }}>
            <div style={{ display: "flex", alignItems: "center", gap: sp.sm }}>
              <Ic n="alert-triangle" s={14} c={h.wn} />
              <span style={{ ...type.sm, color: h.wn, fontWeight: 500 }}>No location set — required for job assignments</span>
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: sp.sm }}>
          <button onClick={useMyLocation} disabled={gpsLoading} style={{ flex: 1, padding: `${sp.sm}px ${sp.md}px`, borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx, cursor: "pointer", ...type.xs, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: sp.xs, opacity: gpsLoading ? 0.6 : 1 }}>
            <Ic n="navigation" s={12} c={h.ac} /> {gpsLoading ? "Getting..." : "Use My Location"}
          </button>
          <button onClick={() => setShowMap(!showMap)} style={{ flex: 1, padding: `${sp.sm}px ${sp.md}px`, borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx, cursor: "pointer", ...type.xs, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: sp.xs }}>
            <Ic n="map-pin" s={12} c={h.ac} /> {showMap ? "Hide Map" : "Pick on Map"}
          </button>
        </div>

        {showMap && <div ref={mapRef} style={{ width: "100%", height: 250, borderRadius: rad.sm, marginTop: sp.sm, border: `1px solid ${h.bd}`, overflow: "hidden" }} />}
      </div>}

      {/* Read-only info */}
      {isTenant && me.pid && <div style={{ background: h.ib, borderRadius: rad.sm, padding: sp.md, marginBottom: sp.md }}>
        <div style={{ ...type.xs, color: h.tt, marginBottom: sp.xs }}>{t.assignedProperty || "Assigned Property"}</div>
        <div style={{ ...type.sm, color: h.tx, fontWeight: 500 }}>{me.pid}</div>
      </div>}

      {isTenant && me.join_date && <div style={{ background: h.ib, borderRadius: rad.sm, padding: sp.md, marginBottom: sp.md }}>
        <div style={{ ...type.xs, color: h.tt, marginBottom: sp.xs }}>{t.joinDate || "Joined"}</div>
        <div style={{ ...type.sm, color: h.tx, fontWeight: 500 }}>{me.join_date || me.join}</div>
      </div>}

      <div style={{ display: "flex", gap: sp.sm, marginTop: sp.md }}>
        <button onClick={save} disabled={saving} style={{ ...btnP(h), flex: 1, opacity: saving ? 0.7 : 1 }}>
          {saving ? "..." : (t.save || "Save")}
        </button>
        <button onClick={() => go("dash")} style={{ ...btnSec(h), flex: 1 }}>
          {t.cancel || "Cancel"}
        </button>
      </div>
    </div>
  </div>;
}
